// Persist login failures across remounts and /login/email-link → /login.
// The completion effect and auth-loading spinner used to wipe in-memory error
// state in about a second; sessionStorage + ?reason= keep the message up.
import { ApiError } from "./api.js";
import {
  isLoginRejectReason,
  loginRejectMessage,
  type LoginRejectReason,
} from "@rrid/shared-types";

export const LOGIN_ERROR_KEY = "rrid_login_error";

export function peekLoginError(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(LOGIN_ERROR_KEY);
    return raw && raw.trim() ? raw : null;
  } catch {
    return null;
  }
}

export function persistLoginError(message: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(LOGIN_ERROR_KEY, message);
  } catch {
    /* private mode */
  }
}

export function clearLoginError(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(LOGIN_ERROR_KEY);
  } catch {
    /* ignore */
  }
}

export function messageForLoginReason(reason: string | null | undefined): string | null {
  if (!isLoginRejectReason(reason)) return null;
  return loginRejectMessage(reason);
}

export function describeEmailLinkCompleteError(err: unknown): { message: string; reason?: LoginRejectReason } {
  if (err instanceof ApiError) {
    if (err.status === 403) {
      const reason =
        err.body && typeof err.body === "object" && "error" in err.body
          ? String((err.body as { error: unknown }).error)
          : "";
      if (isLoginRejectReason(reason)) {
        return { message: loginRejectMessage(reason), reason };
      }
      return { message: loginRejectMessage("not_authorized"), reason: "not_authorized" };
    }
    if (err.status === 401) {
      return { message: "That sign-in link is invalid or expired. Request a new one." };
    }
  }
  const code =
    err && typeof err === "object" && "code" in err ? String((err as { code: unknown }).code) : "";
  if (code === "auth/invalid-action-code" || code === "auth/expired-action-code") {
    return { message: "That sign-in link is invalid or expired. Request a new one." };
  }
  return { message: "Could not complete sign-in. Request a new link from the login page." };
}
